import { useState, type JSX } from "react";
import { useClaws } from "../../hooks/useClaws";
import {
  openStripeUrlInNewTab,
  useCustomerBilling,
  useSlotReturnPoll,
} from "../../hooks/useCustomerBilling";
import { Skeleton } from "../../components/dashboard/Skeleton";
import { SlotCadencePicker } from "../../components/dashboard/billing/SlotCadencePicker";
import { defaultSlotCadence, subscriptionStatusTone, type TenantBillingSummary } from "../../types/billing";
import { coachingUsage } from "./coachingUsage";
import styles from "./DashboardBilling.module.css";

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  trialing: "Trialing",
  past_due: "Past due",
  unpaid: "Unpaid",
  incomplete: "Incomplete",
  incomplete_expired: "Expired",
  canceled: "Canceled",
};

const statusLabel = (status: string): string => STATUS_LABELS[status] ?? status;
// Prefer the tier's display name; the title-cased code is only a fallback for
// summaries cached before tierDisplayName shipped (codes are internal ids).
const tierLabel = (s: Pick<TenantBillingSummary, "tier" | "tierDisplayName">): string => {
  const displayName = s.tierDisplayName?.trim();
  if (displayName !== undefined && displayName.length > 0) return displayName;
  return s.tier ? s.tier.charAt(0).toUpperCase() + s.tier.slice(1) : "—";
};
const formatDate = (iso: string): string =>
  // Render in UTC: cancelAt is a UTC instant, so formatting in the browser's
  // local zone can show the wrong calendar day for an instant near UTC midnight.
  new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

const BillingSkeleton = (): JSX.Element => (
  <div className={styles.stack} aria-hidden>
    <div className={styles.grid}>
      <section className={styles.card}>
        <div className={styles.cardBody}>
          <Skeleton width="45%" height="0.7rem" />
          <Skeleton width="70%" height="0.9rem" />
          <Skeleton width="55%" height="0.9rem" />
        </div>
      </section>
      <section className={styles.card}>
        <div className={styles.cardBody}>
          <Skeleton width="55%" height="0.7rem" />
          <Skeleton width="60%" height="0.8rem" />
          <Skeleton width="100%" height="8px" />
          <Skeleton width="80%" height="0.7rem" />
        </div>
      </section>
    </div>
    <section className={styles.card}>
      <div className={styles.cardBody}>
        <Skeleton width="35%" height="0.7rem" />
        <Skeleton width="45%" height="0.8rem" />
        <Skeleton width="100%" height="8px" />
        <Skeleton width="55%" height="0.7rem" />
      </div>
    </section>
  </div>
);

// One tenant's billing surface: subscription state + the integration-slot
// manager + the Stripe portal link. Split from the page shell so the
// useCustomerBilling hook only mounts once a tenant id is resolved.
const BillingForTenant = ({ tenantId }: { tenantId: string }): JSX.Element => {
  const { status, summary, error, isFetching, refetch, startSlotCheckout, openPortal } =
    useCustomerBilling(tenantId);
  const pendingSlot = useSlotReturnPoll(refetch);
  const [busy, setBusy] = useState<boolean>(false);
  const [message, setMessage] = useState<{ tone: "ok" | "crit"; text: string } | null>(null);
  // Slot cadence choice (IP-389). null until the customer picks one; the
  // effective cadence then defaults to the plan's own (below), which preserves
  // the pre-picker behavior for anyone who never touches the control.
  const [pickedCadence, setPickedCadence] = useState<string | null>(null);

  if (status === "loading" && summary === null) return <BillingSkeleton />;
  if (status === "error" && summary === null) {
    return <div className={styles.errorPanel}>We couldn&apos;t load your billing: {error}</div>;
  }
  if (summary === null) return <BillingSkeleton />;
  const s = summary;

  const onPortal = async (): Promise<void> => {
    setBusy(true);
    setMessage(null);
    const result = await openStripeUrlInNewTab(() => openPortal());
    setBusy(false);
    if (!result.ok) setMessage({ tone: "crit", text: result.message });
  };

  const slotCadence = pickedCadence ?? defaultSlotCadence(s.planInterval, s.slotPrices);

  const onBuy = async (): Promise<void> => {
    setBusy(true);
    setMessage(null);
    const result = await openStripeUrlInNewTab(() =>
      startSlotCheckout("/dashboard/billing", slotCadence ?? undefined),
    );
    setBusy(false);
    if (!result.ok) setMessage({ tone: "crit", text: result.message });
  };

  const tone = subscriptionStatusTone(s.status ?? "");

  // Segmented usage bar: the allowance split by source (included, then bonus
  // grant, then purchased) with the in-use portion solid and the free portion
  // faded, so colour conveys both composition and usage. Capacity is treated as
  // used in that same order, so the free remainder always sits in the
  // last-ranked source. Clamp in-use to the allowance so an over-subscribed
  // tenant (slotsInUse > slotAllowance, e.g. after a tier's includedSlots was
  // lowered) can't push the segment widths past 100% and overflow the bar.
  const bonusSlots = s.bonusSlots ?? 0;
  const inUseForBar = Math.min(s.slotsInUse, s.slotAllowance);
  const includedInUse = Math.min(inUseForBar, s.includedSlots);
  const bonusInUse = Math.min(Math.max(0, inUseForBar - s.includedSlots), bonusSlots);
  const purchasedInUse = Math.max(0, inUseForBar - s.includedSlots - bonusSlots);
  const pctOf = (n: number): number => (s.slotAllowance > 0 ? (n / s.slotAllowance) * 100 : 0);
  const segments = (
    [
      { key: "inc-used", tone: "included", used: true, count: includedInUse },
      { key: "inc-free", tone: "included", used: false, count: s.includedSlots - includedInUse },
      { key: "bon-used", tone: "bonus", used: true, count: bonusInUse },
      { key: "bon-free", tone: "bonus", used: false, count: bonusSlots - bonusInUse },
      { key: "pur-used", tone: "purchased", used: true, count: purchasedInUse },
      { key: "pur-free", tone: "purchased", used: false, count: s.purchasedSlots - purchasedInUse },
    ] as const
  ).filter((seg) => seg.count > 0);

  // Coaching sessions: total (tier baseline) split into used (solid) + remaining
  // (faded). See coachingUsage — total === 0 means the tier includes none.
  const coaching = coachingUsage(s);

  return (
    <div className={`${styles.stack} ${isFetching ? styles.fetching : ""}`}>
      {pendingSlot ? (
        <div className={`${styles.message} ${styles.message_ok}`} role="status">
          Payment received. Your integration slot will appear here in a moment.
        </div>
      ) : null}
      {message ? (
        <div className={`${styles.message} ${styles[`message_${message.tone}`]}`} role="status">
          {message.text}
        </div>
      ) : null}

      <div className={styles.grid}>
        <section className={styles.card}>
          <div className={styles.cardBody}>
            <p className={styles.cardEyebrow}>// SUBSCRIPTION</p>
            <div className={styles.rows}>
              <div className={styles.row}>
                <span className={styles.rowLabel}>Status</span>
                {s.hasSubscription ? (
                  <span className={styles.rowValue}>
                    <span className={styles.dot} data-tone={tone} aria-hidden />
                    {statusLabel(s.status ?? "")}
                  </span>
                ) : (
                  <span className={`${styles.rowValue} ${styles.rowValueMuted}`}>
                    <span className={styles.dot} data-tone="muted" aria-hidden />
                    None
                  </span>
                )}
              </div>
              <div className={styles.row}>
                <span className={styles.rowLabel}>Plan</span>
                <span className={styles.rowValue}>{tierLabel(s)}</span>
              </div>
              {s.cancelAt ? (
                <div className={styles.row}>
                  <span className={styles.rowLabel}>Cancels</span>
                  <span className={`${styles.rowValue} ${styles.rowValueMuted}`}>{formatDate(s.cancelAt)}</span>
                </div>
              ) : null}
            </div>
            {s.hasSubscription ? (
              <div className={styles.billed}>
                <p className={styles.billedLabel}>Billed items</p>
                <ul className={styles.billedList}>
                  <li className={styles.billedItem}>Base plan</li>
                  {s.purchasedSlots > 0 ? (
                    <li className={styles.billedItem}>
                      {s.purchasedSlots} integration slot{s.purchasedSlots === 1 ? "" : "s"}
                    </li>
                  ) : null}
                </ul>
              </div>
            ) : null}
          </div>
          {s.hasSubscription ? (
            <div className={styles.cardFooter}>
              <button
                type="button"
                className={`${styles.linkAction} ${styles.linkMuted}`}
                disabled={busy}
                onClick={onPortal}
              >
                Manage billing &amp; invoices <span className={styles.linkArrow} aria-hidden>&rarr;</span>
              </button>
            </div>
          ) : null}
        </section>

        <section className={styles.card}>
          <div className={styles.cardBody}>
            <p className={styles.cardEyebrow}>// INTEGRATION SLOTS</p>
            {s.unlimited ? (
              <>
                <p className={styles.usageCaption}>
                  <span className={styles.usageCount}>{s.slotsInUse}</span> connected
                </p>
                <p className={styles.unlimitedRow}>
                  <span className={styles.unlimitedBadge}>Unlimited</span>
                  <span className={styles.note}>No slot limit on this instance.</span>
                </p>
              </>
            ) : (
              <>
                <p className={styles.usageCaption}>
                  <span className={styles.usageCount}>{s.slotsInUse}</span> of {s.slotAllowance} in use
                </p>
                <div
                  className={styles.segBar}
                  role="img"
                  aria-label={`${s.slotsInUse} of ${s.slotAllowance} integration slots in use`}
                >
                  {segments.map((seg) => (
                    <span
                      key={seg.key}
                      className={styles.seg}
                      data-tone={seg.tone}
                      data-used={seg.used}
                      style={{ width: `${pctOf(seg.count)}%` }}
                    />
                  ))}
                </div>
                <div className={styles.legend}>
                  <span className={styles.legendItem}>
                    <span className={styles.legendDot} data-tone="included" aria-hidden />
                    <span>
                      <span className={styles.legendVal}>{s.includedSlots}</span> included
                    </span>
                  </span>
                  {bonusSlots > 0 ? (
                    <span className={styles.legendItem}>
                      <span className={styles.legendDot} data-tone="bonus" aria-hidden />
                      <span>
                        <span className={styles.legendVal}>{bonusSlots}</span> bonus
                      </span>
                    </span>
                  ) : null}
                  {s.purchasedSlots > 0 ? (
                    <span className={styles.legendItem}>
                      <span className={styles.legendDot} data-tone="purchased" aria-hidden />
                      <span>
                        <span className={styles.legendVal}>{s.purchasedSlots}</span> purchased
                      </span>
                    </span>
                  ) : null}
                  <span className={styles.legendItem}>
                    <span className={styles.legendDot} data-tone="free" aria-hidden />
                    <span>
                      <span className={styles.legendVal}>{s.slotsRemaining}</span> free
                    </span>
                  </span>
                </div>
              </>
            )}
          </div>
          {s.unlimited ? (
            <div className={styles.cardFooter}>
              <p className={styles.note}>Integration slots are included at no charge on this instance.</p>
            </div>
          ) : s.hasSubscription ? (
            <div className={styles.cardFooter}>
              <SlotCadencePicker
                prices={s.slotPrices}
                value={slotCadence}
                onChange={setPickedCadence}
                disabled={busy}
              />
              <button
                type="button"
                className={`${styles.linkAction} ${styles.linkPrimary}`}
                disabled={busy}
                onClick={() => void onBuy()}
              >
                Buy an integration slot <span className={styles.linkArrow} aria-hidden>&rarr;</span>
              </button>
            </div>
          ) : (
            <div className={styles.cardFooter}>
              <p className={styles.note}>These slots are included with your {tierLabel(s)} plan.</p>
            </div>
          )}
        </section>
      </div>

      <section className={styles.card}>
        <div className={styles.cardBody}>
          <p className={styles.cardEyebrow}>// COACHING SESSIONS</p>
          {s.unlimited ? (
            <p className={styles.unlimitedRow}>
              <span className={styles.unlimitedBadge}>Unlimited</span>
              <span className={styles.note}>Coaching sessions are included on this instance.</span>
            </p>
          ) : !coaching.included ? (
            <p className={styles.note}>
              Coaching sessions aren&apos;t included with your {tierLabel(s)} plan.
            </p>
          ) : (
            <>
              <p className={styles.usageCaption}>
                <span className={styles.usageCount}>{coaching.used}</span> of {coaching.total} used
              </p>
              <div
                className={styles.segBar}
                role="img"
                aria-label={`${coaching.used} of ${coaching.total} coaching sessions used`}
              >
                {coaching.segments.map((seg) => (
                  <span
                    key={seg.key}
                    className={styles.seg}
                    data-tone="included"
                    data-used={seg.used}
                    style={{ width: `${seg.pct}%` }}
                  />
                ))}
              </div>
              <div className={styles.legend}>
                <span className={styles.legendItem}>
                  <span className={styles.legendDot} data-tone="included" aria-hidden />
                  <span>
                    <span className={styles.legendVal}>{coaching.used}</span> used
                  </span>
                </span>
                <span className={styles.legendItem}>
                  <span className={styles.legendDot} data-tone="free" aria-hidden />
                  <span>
                    <span className={styles.legendVal}>{coaching.remaining}</span> remaining
                  </span>
                </span>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export const DashboardBilling = (): JSX.Element => {
  const { claws, status: clawsStatus } = useClaws();
  const tenantId: string | null =
    claws.find((c) => c.status === "RUNNING")?.tenantId ?? claws[0]?.tenantId ?? null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.eyebrow}>// BILLING</span>
        <h1 className={styles.title}>Billing &amp; integration slots</h1>
        <p className={styles.subtitle}>Manage your subscription, integration slots, and invoices.</p>
      </header>
      {clawsStatus === "loading" && claws.length === 0 ? (
        <BillingSkeleton />
      ) : tenantId === null ? (
        <div className={styles.empty}>Launch an instance to manage billing.</div>
      ) : (
        <BillingForTenant tenantId={tenantId} />
      )}
    </div>
  );
};
